import { createSelector } from 'reselect';
import FieldModes from '../constants/FieldModes';
import { swapId } from './utils';
import { isFieldHiddenInView } from './documentHelper';
import { applyLogic } from '@joyfill/conditional-logic';

/**
 * 
 * @param {Array} columns  // Array of column objects
 * @param {Object} fieldPositionColumns // Object containing column settings. iee { columnId: { hidden: true/false } }
 * @param {String} view
 * @param {String} mode - field mode (edit, fill, readonly, pdf)
 * @param {Array} docFields - doc.fields array for conditional logic evaluation
 * @returns Array
 */
export const filterHiddenTableColumns = (columns, fieldPositionColumns, view, mode, docFields) => {
  const evaluated = (mode !== FieldModes.edit && docFields)
    ? applyLogic(columns, docFields, '_id')
    : columns;

  return evaluated.filter((column) => {
    if (mode !== FieldModes.edit && column.hidden) return false;
    if (mode !== FieldModes.edit && isFieldHiddenInView(column?.hiddenViews, view)) return false;
    return true;
  });
}

export const filterHiddenTableColumnsSelector = createSelector(
  (state) => state.columns,
  (state) => state.fieldPositionColumns,
  (state) => state.view,
  (state) => state.mode,
  (state) => state.docFields,
  (columns, fieldPositionColumns, view, mode, docFields) => {
    return filterHiddenTableColumns(columns, fieldPositionColumns, view, mode, docFields);
  }
);

/**
 * Cleans a row object by:
 *  - Removing the "deleted" property
 *  - Removing empty values from the "cells" object
 *
 * @param {Object} row - The row object to clean
 * @returns {Object} - A new row object without "deleted" and cleaned "cells"
 */
export const cleanRow = (row) => {
  if (!row || typeof row !== 'object') return {};

  // Destructure and exclude "deleted"
  const { deleted, cells = {}, ...rest } = row;

  // Build a new cleaned cells object
  const cleanedCells = Object.entries(cells).reduce((acc, [key, value]) => {
    const isEmptyArray = Array.isArray(value) && value.length === 0;
    const isEmptyString = typeof value === 'string' && value.trim() === '';

    // Keep only non-empty values
    if (value !== null && value !== undefined && !isEmptyArray && !isEmptyString) {
      acc[key] = value;
    }

    return acc;
  }, {});

  // Return new object (immutability preserved)
  return {
    ...rest,
    cells: cleanedCells,
  };
};


/**
 * For Document fieldPositions that have multiple table cell fieldPositions associated
 * with the same table field, we need to keep the column property in sync.
 * 
 * @param {*} nextDoc 
 * @param {*} fileId 
 * @param {*} fieldId 
 * @param {*} columnChanges 
 */
export const syncTableFieldColumnIdChangeInAllViewFieldPositions = (nextDoc, fileId, fieldId, columnChanges) => {

  const { oldColumnId, newColumnId } = columnChanges;

  const fileIndex = nextDoc.files.findIndex(f => f._id === fileId);

  const updateFieldPositionColumnId = (fp) => {
    if (fp.field !== fieldId) return fp;
    const nextFp = { ...fp };

    /**
     * 1. Update the fieldPostion column property to the newColumnId if it matches the oldColumnId
     */
    if (nextFp.column === oldColumnId) nextFp.column = newColumnId;

    /**
     * 2. Update the tableColumns property to the newColumnId if it matches the oldColumnId
     */
    if (nextFp.tableColumns && nextFp.tableColumns[oldColumnId]) nextFp.tableColumns = swapId(nextFp.tableColumns, oldColumnId, newColumnId);

    return nextFp;

  }

  // Update primary view fieldPositions
  nextDoc.files[fileIndex].pages = nextDoc.files[fileIndex].pages.map(page => { 
    page.fieldPositions = page.fieldPositions.map(updateFieldPositionColumnId); 
    return page; 
  });

  // Update mobile view fieldPositions if they exist
  if (nextDoc.files[fileIndex].views && nextDoc.files[fileIndex].views.length > 0) {
    nextDoc.files[fileIndex].views = nextDoc.files[fileIndex].views.map(view => {
      if (view.pages) {
        view.pages = view.pages.map(page => {
          page.fieldPositions = page?.fieldPositions.map(updateFieldPositionColumnId);
          return page;
        });
      }
      return view;
    });
  }

  return nextDoc;

}